import type { ButtonHTMLAttributes, Ref } from 'react'

import { cn } from '@/lib/cn'

import { Spinner } from './Spinner'

export type IconButtonVariant = 'ghost' | 'secondary' | 'primary' | 'danger'
export type IconButtonSize = 'sm' | 'md' | 'lg'

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required: an icon-only control has no text for assistive tech to read. */
  'aria-label': string
  variant?: IconButtonVariant
  size?: IconButtonSize
  loading?: boolean
  /** Renders as a circle. Used for the play/pause control. */
  round?: boolean
  ref?: Ref<HTMLButtonElement>
}

const VARIANTS: Record<IconButtonVariant, string> = {
  ghost: 'text-text-muted hover:bg-surface-hover hover:text-text',
  secondary: 'border border-border bg-surface-raised text-text hover:bg-surface-hover',
  primary: 'bg-accent text-on-accent hover:bg-accent-hover',
  danger: 'text-danger hover:bg-surface-hover',
}

const SIZES: Record<IconButtonSize, string> = {
  // The pseudo-element pushes the pointer target out to 44px while the painted
  // button stays visually small enough for dense rows.
  sm: "h-9 w-9 relative before:absolute before:-inset-1 before:content-['']",
  md: 'h-11 w-11',
  lg: 'h-12 w-12',
}

export function IconButton({
  variant = 'ghost',
  size = 'md',
  loading = false,
  round = false,
  className,
  disabled,
  children,
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center transition-colors',
        round ? 'rounded-full' : 'rounded-lg',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size="sm" label={null} /> : children}
    </button>
  )
}
